import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  useTheme,
  Avatar,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  Badge,
  TableSortLabel,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  FamilyRestroom as FamilyIcon,
  EmojiEvents as TrophyIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import api from '../config/api.js';
import { formatCurrency } from '../utils/formatters';
import DatePickerField from '../components/common/DatePickerField';

// Format dates for display (MM/DD/YYYY)
const formatDateForDisplay = (dateStr, dateType) => {
  if (!dateStr) return '';
  
  // Parse YYYY-MM-DD HH:mm:ss format
    const [datePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
};

const getDateRangeTitle = (startDate, endDate, dateRange) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Parse YYYY-MM-DD safely to avoid timezone issues
  const parseYMD = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const start = parseYMD(startDate);
  const end = parseYMD(endDate);

  // Format date as "MMM d, yyyy" (e.g., "Jan 1, 2024")
  const formatDate = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  };

  // Format month as "MMM yyyy" (e.g., "Jan 2024")
  const formatMonth = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
  };

  // Helper function to compare dates ignoring time
  const isSameDay = (date1, date2) => {
    return date1 && date2 &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  switch (dateRange) {
    case 'day':
      if (isSameDay(end, today)) {
        return 'Today';
      } else if (isSameDay(end, yesterday)) {
        return 'Yesterday';
      } else {
        return formatDate(end);
      }
    case 'week': {
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay());
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

      if (isSameDay(end, thisWeekEnd)) {
        return 'This Week';
      } else if (isSameDay(end, lastWeekEnd)) {
        return 'Last Week';
      } else {
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
    }
    case 'month': {
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      if (end && end.getFullYear() === thisMonthEnd.getFullYear() && end.getMonth() === thisMonthEnd.getMonth()) {
        return 'This Month';
      } else if (end && end.getFullYear() === lastMonthEnd.getFullYear() && end.getMonth() === lastMonthEnd.getMonth()) {
        return 'Last Month';
      } else {
        return formatMonth(end);
      }
    }
    default:
      return `${formatDate(start)} - ${formatDate(end)}`;
  }
};

const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 140, sm: 140 },
        bgcolor: isDark ? 'background.paper' : 'white',
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography 
          component="h2" 
          variant="h6" 
          gutterBottom
          sx={{ 
            color: color,
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}
        >
          {title}
        </Typography>
        <Box sx={{ 
          color: color,
          '& svg': {
            fontSize: { xs: '1.8rem', sm: '1.5rem' }
          }
        }}>
          {icon}
        </Box>
      </Box>
      <Typography 
        component="p" 
        variant="h4" 
        color="text.primary"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.5rem' }
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

const WriterCard = ({ rank, name, production, avatar }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = {
    1: '#FFD700', // Gold
    2: '#C0C0C0', // Silver
    3: '#CD7F32', // Bronze
  };

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: { xs: 80, sm: 100 },
        bgcolor: isDark ? 'background.paper' : 'white',
      }}
    >
      <Avatar sx={{ 
        width: { xs: 40, sm: 56 }, 
        height: { xs: 40, sm: 56 }, 
        bgcolor: colors[rank], 
        color: 'white' 
      }}>
        <TrophyIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }} />
      </Avatar>
      <Box>
        <Typography variant="h6" sx={{ 
          color: colors[rank],
          fontSize: { xs: '0.95rem', sm: '1.25rem' }
        }}>
          #{rank} {name}
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{
          fontSize: { xs: '0.85rem', sm: '1rem' }
        }}>
          {production}
        </Typography>
      </Box>
    </Paper>
  );
};

const WriterTableRow = ({ writer, closes }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          },
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {writer.name}
        </TableCell>
        <TableCell align="right">{formatCurrency(writer.totalAP)}</TableCell>
        <TableCell align="right">{writer.closes}</TableCell>
        <TableCell>{writer.team}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Closes Breakdown
              </Typography>
              <Table size="small" aria-label="closes">
                <TableHead>
                  <TableRow>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Submit Date</TableCell>
                    <TableCell>Agency</TableCell>
                    <TableCell>Carrier</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell align="right">AP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closes.map((close, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDateForDisplay(close.effectiveDate, 'effective')}</TableCell>
                      <TableCell>{formatDateForDisplay(close.submitDate, 'submit')}</TableCell>
                      <TableCell>{close.agency}</TableCell>
                      <TableCell>{close.carrier}</TableCell>
                      <TableCell>{close.product}</TableCell>
                      <TableCell>{close.state}</TableCell>
                      <TableCell align="right">{formatCurrency(close.ap)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const Dashboard = ({ onTeamChange }) => {
  const theme = useTheme();
  
  // Get first and last day of current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  // Format dates for input fields
  const formatDateForInput = (date) => {
    if (!date) return '';
    // Always use YYYY-MM-DD format for the date picker
    return date.toISOString().split('T')[0];
  };

  // Parse date string based on format
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    // Parse from YYYY-MM-DD format
    return new Date(dateStr);
  };

  const [stats, setStats] = useState(null);
  const [topAgents, setTopAgents] = useState([]);
  const [writerDetails, setWriterDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allCloses, setAllCloses] = useState([]);
  const [filters, setFilters] = useState({
    team: 'All Teams',
    carrier: '',
    product: '',
    dateType: 'submit',
    startDate: formatDateForInput(firstDayOfMonth),
    endDate: formatDateForInput(lastDayOfMonth),
  });
  const [filterOptions, setFilterOptions] = useState({
    teams: [],
    carriers: [],
    products: [],
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilters, setActiveFilters] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('totalAP');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('month');

  // Update active filters count whenever filters change
  useEffect(() => {
    const count = [filters.team, filters.carrier, filters.product].filter(Boolean).length;
    setActiveFilters(count);
  }, [filters.team, filters.carrier, filters.product]);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/Closes');
        const closesData = response.data;

        if (!Array.isArray(closesData)) {
          throw new Error('Invalid data format received from server');
        }

        // Extract unique values for filters
        const teams = [...new Set(closesData.map(close => close.team))].filter(Boolean);
        const carriers = [...new Set(closesData.map(close => close.carrier))].filter(Boolean);
        const products = [...new Set(closesData.map(close => close.product))].filter(Boolean);

        setFilterOptions({
          teams,
          carriers,
          products,
        });

        setAllCloses(closesData);
        
        // Initialize filters with team from localStorage if available
        const savedTeam = localStorage.getItem('selectedTeam');
        const initialFilters = {
          ...filters,
          team: savedTeam || 'All Teams'
        };
        setFilters(initialFilters);
        updateFilteredData(closesData, initialFilters);
        
        // Ensure team change is called with initial value
        if (savedTeam) {
          onTeamChange(savedTeam);
        } else {
          onTeamChange('All Teams');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const updateFilteredData = (closes, currentFilters) => {
    // Filter closes based on current filters
    const filteredCloses = closes.filter(close => {
      const matchesTeam = currentFilters.team === 'All Teams' || !currentFilters.team || close.team.toUpperCase() === currentFilters.team.toUpperCase();
      const matchesCarrier = !currentFilters.carrier || close.carrier === currentFilters.carrier;
      const matchesProduct = !currentFilters.product || close.product === currentFilters.product;
      
      // Date filtering
      let matchesDate = true;
      if (currentFilters.dateType && (currentFilters.startDate || currentFilters.endDate)) {
        const dateField = currentFilters.dateType === 'submit' ? 'submitDate' : 'effectiveDate';
        const dateStr = close[dateField];
        
        if (!dateStr) {
          matchesDate = false;
        } else {
          // Parse YYYY-MM-DD HH:mm:ss format
          const [datePart, timePart] = dateStr.split(' ');
          const [year, month, day] = datePart.split('-');
          const closeDate = new Date(year, month - 1, day); // month is 0-based in JS Date
          
          if (currentFilters.startDate) {
            const startDate = new Date(currentFilters.startDate);
            startDate.setHours(0, 0, 0, 0);
            matchesDate = matchesDate && closeDate >= startDate;
          }
          
          if (currentFilters.endDate) {
            const endDate = new Date(currentFilters.endDate);
            endDate.setHours(23, 59, 59, 999);
            matchesDate = matchesDate && closeDate <= endDate;
          }
        }
      }

      return matchesTeam && matchesCarrier && matchesProduct && matchesDate;
    });

    // Calculate stats
    const totalAP = filteredCloses.reduce((sum, record) => sum + (Number(record.ap) || 0), 0);
    const uniqueAgents = new Set(filteredCloses.map(record => record.agent)).size;
    const uniqueAgencies = new Set(filteredCloses.map(record => record.agency)).size;

    setStats({
      totalAP,
      uniqueAgents,
      uniqueAgencies,
      totalCloses: filteredCloses.length
    });

    // Calculate top agents
    const agentStats = filteredCloses.reduce((acc, record) => {
      const agent = record.agent;
      if (!acc[agent]) {
        acc[agent] = {
          name: agent,
          totalAP: 0,
          closes: 0,
          team: record.team,
          latestSubmitDate: record.submitDate
        };
      }
      acc[agent].totalAP += Number(record.ap) || 0;
      acc[agent].closes += 1;
      
      if (record.submitDate && (!acc[agent].latestSubmitDate || record.submitDate > acc[agent].latestSubmitDate)) {
        acc[agent].team = record.team;
        acc[agent].latestSubmitDate = record.submitDate;
      }
      
      return acc;
    }, {});

    const topAgentsData = Object.values(agentStats)
      .sort((a, b) => b.totalAP - a.totalAP)
      .slice(0, 3);

    setTopAgents(topAgentsData);

    // Group closes by agent
    const closesByAgent = filteredCloses.reduce((acc, close) => {
      if (!acc[close.agent]) {
        acc[close.agent] = [];
      }
      acc[close.agent].push(close);
      return acc;
    }, {});

    setWriterDetails(closesByAgent);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;
    
    // Normalize team value to uppercase when setting the filter
    if (name === 'team') {
      newValue = value === 'All Teams' ? 'All Teams' : (value ? value.toUpperCase() : 'All Teams');
    }
    
    const newFilters = {
      ...filters,
      [name]: newValue
    };
    
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
    
    // Call onTeamChange after updating filters to ensure state is in sync
    if (name === 'team') {
      onTeamChange(newValue);
    }
  };

  const handleTabChange = (event, newValue) => {
    const newFilters = {
      ...filters,
      dateType: newValue
    };
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const isAsc = order === 'asc';
      let comparison = 0;

      switch (orderBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'totalAP':
          comparison = a.totalAP - b.totalAP;
          break;
        case 'closes':
          comparison = a.closes - b.closes;
          break;
        case 'team':
          comparison = a.team.localeCompare(b.team);
          break;
        default:
          comparison = 0;
      }

      return isAsc ? comparison : -comparison;
    });
  };

  const filterAndSortWriters = () => {
    const filteredWriters = topAgents.filter(writer =>
      writer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      writer.team.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filteredWriters);
  };

  // Add clear filters handler
  const handleClearFilters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/Closes');
      const closesData = response.data;

      if (!Array.isArray(closesData)) {
        throw new Error('Invalid data format received from server');
      }
      
      // Extract unique values for filters
      const teams = [...new Set(closesData.map(close => close.team))].filter(Boolean);
      const carriers = [...new Set(closesData.map(close => close.carrier))].filter(Boolean);
      const products = [...new Set(closesData.map(close => close.product))].filter(Boolean);

      setFilterOptions({
        teams,
        carriers,
        products,
      });

      setAllCloses(closesData);

      const newFilters = {
        team: 'All Teams',
        carrier: '',
        product: '',
        dateType: filters.dateType, // Keep the current date type
        startDate: formatDateForInput(firstDayOfMonth),
        endDate: formatDateForInput(lastDayOfMonth),
      };
      setFilters(newFilters);
      updateFilteredData(closesData, newFilters);
      
      // Reset team to All Teams
      onTeamChange('All Teams');
      
      handleFilterClose();
    } catch (err) {
      console.error('Error clearing filters:', err);
      setError(err.message || 'Failed to clear filters');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate, endDate;

    switch (range) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6); // End of week (Saturday)
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      default:
        return;
    }

    const newFilters = {
      ...filters,
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    };
    setFilters(newFilters);
    setDateRange(range);
    updateFilteredData(allCloses, newFilters);
  };

  const handleDateShift = (direction) => {
    const currentStartDate = new Date(filters.startDate);
    const currentEndDate = new Date(filters.endDate);
    const shiftAmount = direction === 'forward' ? 1 : -1;

    let newStartDate, newEndDate;

    switch (dateRange) {
      case 'day':
        newStartDate = new Date(currentStartDate);
        newStartDate.setDate(currentStartDate.getDate() + shiftAmount);
        newEndDate = new Date(newStartDate);
        break;
      case 'week':
        newStartDate = new Date(currentStartDate);
        newStartDate.setDate(currentStartDate.getDate() + (shiftAmount * 7));
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newStartDate.getDate() + 6);
        break;
      case 'month':
        console.log('=== Month Navigation Debug ===');
        console.log('Direction:', direction);
        console.log('Current End Date:', currentEndDate);
        console.log('Current Month:', currentEndDate.getMonth());
        console.log('Current Year:', currentEndDate.getFullYear());
        
        // Calculate the new month and year based on end date
        let newMonth = currentEndDate.getMonth() + shiftAmount;
        let newYear = currentEndDate.getFullYear();
        
        // Handle month overflow/underflow
        if (newMonth > 11) {
          newMonth = 0;
          newYear++;
        } else if (newMonth < 0) {
          newMonth = 11;
          newYear--;
        }
        
        // Create new dates with explicit year, month, and day
        newStartDate = new Date(newYear, newMonth, 1);
        newEndDate = new Date(newYear, newMonth + 1, 0);
        
        console.log('New Start Date:', newStartDate);
        console.log('New End Date:', newEndDate);
        console.log('New Month:', newEndDate.getMonth());
        console.log('New Year:', newEndDate.getFullYear());
        console.log('======================\n');
        break;
      default:
        return;
    }

    const formattedStartDate = formatDateForInput(newStartDate);
    const formattedEndDate = formatDateForInput(newEndDate);

    const newFilters = {
      ...filters,
      startDate: formattedStartDate,
      endDate: formattedEndDate
    };
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      px: { xs: 1, sm: 2, md: 3 }, // Responsive padding
      boxSizing: 'border-box'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        gap: 2,
        width: '100%',
        pt: { xs: 3, sm: 1 }, // Add more top padding on mobile screens
      }}>
        <Typography 
          variant="h4" 
          color="text.primary" 
          sx={{ 
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
        >
          {getDateRangeTitle(filters.startDate, filters.endDate, dateRange)}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}>
          <IconButton
            onClick={handleFilterClick}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Badge badgeContent={activeFilters} color="primary">
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            p: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Team</InputLabel>
            <Select
              name="team"
              value={filters.team}
              label="Team"
              onChange={handleFilterChange}
              sx={{
                '& .MuiSelect-select': {
                  color: 'primary.main',
                },
              }}
            >
              <MenuItem value="All Teams">
                All Teams
              </MenuItem>
              {filterOptions.teams.map(team => (
                <MenuItem 
                  key={team} 
                  value={team.toUpperCase()}
                  sx={{
                    color: team.toUpperCase() === filters.team ? 'primary.main' : 'inherit',
                  }}
                >
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Carrier</InputLabel>
            <Select
              name="carrier"
              value={filters.carrier}
              label="Carrier"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              {filterOptions.carriers.map(carrier => (
                <MenuItem key={carrier} value={carrier}>{carrier}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Product</InputLabel>
            <Select
              name="product"
              value={filters.product}
              label="Product"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              {filterOptions.products.map(product => (
                <MenuItem key={product} value={product}>{product}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={filters.dateType}
            exclusive
            onChange={handleTabChange}
            size="small"
            fullWidth
            sx={{
              mb: 1,
              '& .MuiToggleButton-root': {
                flex: 1,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="submit">Submit Date</ToggleButton>
            <ToggleButton value="effective">Effective Date</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleDateShift('back')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <ToggleButtonGroup
              value={dateRange}
              exclusive
              onChange={(e, value) => value && handleDateRangeChange(value)}
              size="small"
              sx={{
                flex: 1,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>

            <IconButton
              size="small"
              onClick={() => handleDateShift('forward')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>

          <DatePickerField
            label="Start Date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
          />

          <DatePickerField
            label="End Date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
          />

          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            sx={{
              mt: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              },
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      </Menu>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Production"
            value={formatCurrency(stats?.totalAP || 0)}
            icon={<TrendingUpIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Agents"
            value={stats?.uniqueAgents || 0}
            icon={<PeopleIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Families Protected"
            value={stats?.totalCloses || 0}
            icon={<FamilyIcon />}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

 
    </Box>
  );
};

export default Dashboard; 